import React, { useState } from 'react';
import { Loader2, ThumbsUp, ThumbsDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import StarRating from '@/components/testimonials/StarRating';
import { RATING_COMMENT_MAX, ratingErrorMessage, submitContractorRating } from '@/lib/contractorRatings';

interface Props {
  es: boolean;
  listingId: string;
  homeownerId: string;
  contractorId: string;
  contractorName: string;
  onDone: (rated: boolean) => void;
}

/**
 * Inline rating step inside ProjectClosingDialog. Shown only after the project
 * was saved as 'work_completed' with a GigGone contractor selected, so the
 * server-side guard will accept the insert. Skipping is always allowed.
 */
const ContractorRatingStep: React.FC<Props> = ({ es, listingId, homeownerId, contractorId, contractorName, onDone }) => {
  const [rating, setRating] = useState(0);
  const [again, setAgain] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = rating >= 1 && again !== null && !saving;

  const submit = async () => {
    if (!canSubmit || again === null) return;
    setSaving(true);
    setError('');
    const res = await submitContractorRating({ listingId, homeownerId, contractorId, rating, wouldHireAgain: again, comment });
    setSaving(false);
    if (res.error) {
      setError(ratingErrorMessage(res.error, es));
      return;
    }
    onDone(true);
  };

  const choice = (val: boolean, label: string, Icon: typeof ThumbsUp) => (
    <button
      type="button"
      onClick={() => setAgain(val)}
      aria-pressed={again === val}
      className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition ${
        again === val
          ? val
            ? 'border-emerald-500 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-500'
            : 'border-slate-500 bg-slate-100 text-slate-800 ring-1 ring-slate-500'
          : 'border-slate-200 text-slate-600 hover:border-slate-300'
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <>
      <div className="space-y-6 px-6 py-5">
        <p className="flex items-start gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          {es ? 'Proyecto marcado como terminado. ¿Cómo fue tu experiencia?' : 'Project marked as completed. How did it go?'}
        </p>

        <div>
          <p className="text-sm font-bold text-slate-900">
            {es ? `Califica a ${contractorName}` : `Rate ${contractorName}`} <span className="text-xs font-semibold text-red-600">*</span>
          </p>
          <StarRating value={rating} onChange={setRating} size="lg" className="mt-2" />
        </div>

        <div>
          <p className="text-sm font-bold text-slate-900">
            {es ? '¿Lo contratarías de nuevo?' : 'Would you hire them again?'} <span className="text-xs font-semibold text-red-600">*</span>
          </p>
          <div className="mt-2 flex gap-2">
            {choice(true, es ? 'Sí' : 'Yes', ThumbsUp)}
            {choice(false, 'No', ThumbsDown)}
          </div>
        </div>

        <div>
          <label htmlFor="rating-comment" className="text-sm font-bold text-slate-900">
            {es ? 'Comentario (opcional)' : 'Comment (optional)'}
          </label>
          <textarea
            id="rating-comment"
            rows={3}
            maxLength={RATING_COMMENT_MAX}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={es ? 'Puntualidad, calidad, comunicación…' : 'Timeliness, quality, communication…'}
            className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500"
          />
          <p className="mt-1 text-right text-[11px] text-slate-400">{comment.length}/{RATING_COMMENT_MAX}</p>
          <p className="text-xs text-slate-400">
            {es
              ? 'Tu calificación la ven el contratista y el equipo de GigGone. Se usa para su reputación.'
              : 'Your rating is visible to the contractor and the GigGone team, and builds their reputation.'}
          </p>
        </div>

        {error && (
          <p role="alert" className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
          </p>
        )}
      </div>

      <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:justify-end">
        <button onClick={() => onDone(false)} disabled={saving} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
          {es ? 'Omitir por ahora' : 'Skip for now'}
        </button>
        <button
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {es ? 'Enviar calificación' : 'Submit rating'}
        </button>
      </div>
    </>
  );
};

export default ContractorRatingStep;
